// WHIT-505: the package-tour pages' addresses. One set of pages renders both
// products (pages/package-tours/); the address names the product instead of
// a query parameter:
//
//              English            Hungarian
//   package    /package-tours/…   /csomagtura/…
//   hotel only /hotel-only/…      /csak-szallas/…
//
// e.g. the hotel list /csomagtura/szallodak/ and /csak-szallas/szallodak/,
// the hotel page /csomagtura/szalloda/<name>/<id>/ and
// /csak-szallas/<name>/<id>/.
//
// The proxy turns each address into the page Next.js renders, adding
// product=hotel for a hotel-only one, so `product` never shows in an
// address. The earlier addresses (/hu/csomagos-utazas/…, hotel only under
// /package-tours/?product=hotel) are dropped, not redirected (operator,
// 2026-10-25). WHIT-524: the Hungarian hotel list and package hotel page say
// "hotels"/"hotel" (szallodak/, szalloda/); WHIT-505's talalatok/ and
// szallas/ are dropped too (operator, 2026-11-03). Pure, so `node --test`
// can load it.

// The pages' own routes.
const INTERNAL = '/package-tours/';

const SECTIONS = {
  package: { en: '/package-tours/', hu: '/csomagtura/' },
  hotel: { en: '/hotel-only/', hu: '/csak-szallas/' },
};

// Each page's part of the address. The hotel page continues with
// <name>/<id>/; for hotel only it has no part of its own, as the section
// already says "accommodation": /csak-szallas/<name>/<id>/ (operator,
// 2026-10-25). Longer parts first, so booking/confirmation/ is not taken
// for booking/; the hotel page last, so a page's own part always wins.
const PAGES = [
  { en: 'destinations/', hu: 'celpontok/' },
  { en: 'hotels/', hu: 'szallodak/' },
  { en: 'booking/confirmation/', hu: 'foglalas/visszaigazolas/' },
  { en: 'booking/', hu: 'foglalas/' },
  { en: 'enquiry/', hu: 'erdeklodes/' },
  { en: 'hotel/', hu: 'szalloda/', hotel: true },
];

const HOTEL_TAIL = /^([^/?#]+)\/([0-9A-Za-z_-]{1,20})\/$/;

const language = locale => (locale === 'hu' ? 'hu' : 'en');
const productOf = product => (product === 'hotel' ? 'hotel' : 'package');

// The page's part and what follows it (the hotel page's <name>/<id>/), or
// null when `rest` is no page's.
const partOf = (page, lang, product) =>
  page.hotel && product === 'hotel' ? '' : page[lang];

const pageOf = (rest, lang, product) => {
  for (const page of PAGES) {
    const part = partOf(page, lang, product);
    if (page.hotel) {
      if (rest.startsWith(part) && HOTEL_TAIL.test(rest.slice(part.length)))
        return { page, tail: rest.slice(part.length) };
    } else if (rest === part) {
      return { page, tail: '' };
    }
  }
  return null;
};

// A page's route (/package-tours/hotels/) as the address a visitor sees, in
// `locale`, for `product`. A route that is no product page (the favourites)
// comes back as it is.
/**
 * @param {string} route
 * @param {string | undefined} locale
 * @param {string | null} [product]  'hotel' for hotel only
 * @returns {string}
 */
const productPath = (route, locale, product) => {
  if (!String(route).startsWith(INTERNAL)) return route;
  const found = pageOf(String(route).slice(INTERNAL.length), 'en', 'package');
  if (!found) return route;
  const lang = language(locale);
  const which = productOf(product);
  return `${SECTIONS[which][lang]}${partOf(found.page, lang, which)}${found.tail}`;
};

// The other way: an address (no locale prefix, no query) as the route to
// render, its product and the address's language; null when it is no
// product page's address.
/**
 * @param {string} path
 * @returns {{ route: string, product: 'package' | 'hotel', locale: 'en' | 'hu' } | null}
 */
const productRoute = path => {
  const value = String(path || '');
  for (const product of /** @type {const} */ (['package', 'hotel'])) {
    for (const lang of /** @type {const} */ (['en', 'hu'])) {
      const section = SECTIONS[product][lang];
      if (!value.startsWith(section)) continue;
      const found = pageOf(value.slice(section.length), lang, product);
      if (found)
        return {
          route: `${INTERNAL}${found.page.en}${found.tail}`,
          product,
          locale: lang,
        };
    }
  }
  return null;
};

// The same page in another language, for the language switch; null when
// `path` is no product page's address.
/**
 * @param {string} path  the address bar's path, without the query
 * @param {string} locale  the language to switch to
 */
const translateProductPath = (path, locale) => {
  const found = productRoute(path);
  return found ? productPath(found.route, locale, found.product) : null;
};

// A search's query for an address: the address already says the product.
/**
 * @template {Record<string, unknown>} T
 * @param {T} query
 * @returns {Omit<T, 'product'>}
 */
const withoutProduct = query => {
  const { product, ...rest } = query || {};
  return /** @type {Omit<T, 'product'>} */ (rest);
};

module.exports = {
  INTERNAL,
  SECTIONS,
  productPath,
  productRoute,
  translateProductPath,
  withoutProduct,
};
